import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useThemeMode } from '../contexts/ThemeContext';

interface EmptyStateProps {
  icon: React.ReactNode;
  code: string;
  title: string;
  subtitle: string;
  action?: { label: string; onClick: () => void };
  compact?: boolean;
}

const EmptyState: React.FC<EmptyStateProps> = ({ icon, code, title, subtitle, action, compact = false }) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const border  = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const textP   = isDark ? '#EFEFEF' : '#0A0A0A';
  const textS   = isDark ? '#888888' : '#666666';
  const textM   = isDark ? '#444444' : '#BBBBBB';
  const iconBg  = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      py: compact ? 5 : 8, px: 3, textAlign: 'center',
    }}>
      {/* Icon box */}
      <Box sx={{
        width: compact ? 40 : 48, height: compact ? 40 : 48,
        borderRadius: '10px',
        bgcolor: iconBg,
        border: `1px solid ${border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        mb: compact ? 2 : 3,
        color: textM,
      }}>
        {icon}
      </Box>

      {/* Code badge */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: compact ? 1 : 1.5 }}>
        <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: textM, flexShrink: 0 }} />
        <Typography sx={{
          fontFamily: '"JetBrains Mono","Courier New",monospace',
          fontSize: '0.65rem', fontWeight: 600,
          color: textM, letterSpacing: '0.06em',
        }}>
          {code}
        </Typography>
      </Box>

      <Typography sx={{
        fontSize: compact ? '0.875rem' : '1rem',
        fontWeight: 600, color: textP,
        letterSpacing: '-0.02em', mb: 0.75,
      }}>
        {title}
      </Typography>

      <Typography sx={{
        fontSize: '0.82rem', color: textS, lineHeight: 1.65,
        maxWidth: compact ? 260 : 320,
      }}>
        {subtitle}
      </Typography>

      {action && (
        <Button
          onClick={action.onClick}
          sx={{
            mt: 3, py: 0.875, px: 2.5,
            fontSize: '0.82rem', fontWeight: 500,
            bgcolor: isDark ? '#EFEFEF' : '#0A0A0A',
            color: isDark ? '#0A0A0A' : '#EFEFEF',
            borderRadius: '7px',
            textTransform: 'none',
            '&:hover': { bgcolor: isDark ? '#FFFFFF' : '#1A1A1A' },
          }}
        >
          {action.label}
        </Button>
      )}
    </Box>
  );
};

export default EmptyState;
